import React, { useState } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import IconCheck from 'cccisd-icons/checkmark4';
import IconCross from 'cccisd-icons/cross4';
import IconSpinner from 'cccisd-icons/spinner3';
import IconText from 'cccisd-icons/file-text2';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import Spinner from 'cccisd-spinner';
import Table from 'cccisd-table';
import Tooltip from 'cccisd-tooltip';

import TestRunQuery from './testRuns.graphql';
import style from './style.css';

let reloadTimer = 0;
const reloadTimeout = 5000;

function renderSpinner() {
    return (
        <span style={{ height: '18px', width: '28px', display: 'inline-block' }}>
            <Spinner options={{ position: 'relative', top: '0.7em', left: '0.7em' }} />
        </span>
    );
}

export const JobStatus = ({ value, loadData }) => {
    if (parseInt(value, 10) < 2) {
        if (reloadTimer === 0) {
            reloadTimer = setTimeout(() => {
                loadData();
                reloadTimer = 0;
            }, reloadTimeout);
        }
    }
    if (parseInt(value, 10) === 1) {
        return (
            <>
                {renderSpinner()}
                <span>Processing</span>
            </>
        );
    }
    if (parseInt(value, 10) === 2) {
        return '✅ Success';
    }
    if (parseInt(value, 10) === 3) {
        return '❌ Failed';
    }
    return (
        <>
            {renderSpinner()}
            <span>Queued</span>
        </>
    );
};

function AllTestRuns({ data }) {
    if (!data || !Array.isArray(data)) {
        return <Loader loading type="inline" />;
    }

    return (
        <Table
            rowKey="testRunId"
            name="testRuns"
            orderBy="testRunId"
            isAscentOrder={false}
            showPerPageOptions={false}
            noRecordsMessage="No test results"
            saveState={false}
            data={data}
            columns={[
                { name: 'testRunId', label: 'ID', sort: true, filter: true, class: style.smallColumn },
                {
                    name: 'status',
                    label: 'Status',
                    sort: true,
                    filter: true,
                    filterSettings: {
                        type: 'selectbox',
                        placeholder: '-- Choose status --',
                        options: [
                            { label: 'No Data', value: 0 },
                            { label: 'In Progress', value: 1 },
                            { label: 'Success', value: 2 },
                            { label: 'Failure', value: 3 },
                        ],
                    },
                    render: value => {
                        let content;
                        switch (value) {
                            case 1:
                                content = (
                                    <span>
                                        <IconSpinner spaceRight />
                                        In Progress
                                    </span>
                                );
                                break;
                            case 2:
                                content = (
                                    <span>
                                        <span className="text-success">
                                            <IconCheck spaceRight />
                                        </span>
                                        Success
                                    </span>
                                );
                                break;
                            case 3:
                                content = (
                                    <span>
                                        <span className="text-danger">
                                            <IconCross spaceRight />
                                        </span>
                                        Failed
                                    </span>
                                );
                                break;
                            default:
                                content = <span>-</span>;
                        }

                        return content;
                    },
                },
                {
                    name: 'consoleOutput',
                    label: 'Output',
                    sort: false,
                    filter: true,
                    class: style.mediumColumn,
                    render: (value, row) => {
                        return (
                            <Modal
                                title={`Test Run updated on ${row.updatedAt}`}
                                trigger={
                                    <button type="button" className="btn btn-secondary">
                                        <IconText spaceRight />
                                        Output
                                    </button>
                                }
                                render={() => {
                                    return (
                                        <p className="p-2 font-monospace bg-body-secondary rounded">
                                            {typeof value === 'string' ? value : JSON.stringify(value)}
                                        </p>
                                    );
                                }}
                            />
                        );
                    },
                },
                {
                    name: 'testSuites',
                    label: 'Test Suites',
                    sort: false,
                    filter: true,
                },
                {
                    name: 'testCases',
                    label: 'Test Cases',
                    sort: false,
                    filter: true,
                },
                {
                    name: 'startedAt',
                    label: 'Started',
                    sort: true,
                    filter: true,
                    filterSettings: { type: 'date' },
                },
                {
                    name: 'updatedAt',
                    label: 'Last Updated',
                    sort: true,
                    filter: true,
                    filterSettings: { type: 'date' },
                },
            ]}
        />
    );
}

async function fetchAllTestRuns(testableId, testableType, setData) {
    const resp = await client.query({
        query: TestRunQuery,
        variables: { testableId, testableType },
        fetchPolicy: 'network-only',
    });

    let allTestRuns = resp?.data?.monitoring?.devopsTestRunList || [];
    allTestRuns = allTestRuns.map(testRun => {
        testRun.consoleOutput = JSON.parse(testRun.consoleOutput);
        testRun.testSuites = JSON.parse(testRun.testSuites);

        // TBD format of testCases and testSuites
        if ('testCases' in testRun.testSuites && Array.isArray(testRun.testSuites.testCases)) {
            testRun.testCases = testRun.status === 2 ? '20/20 passed' : '19/20 passed';
        }

        if ('testSuites' in testRun.testSuites && Array.isArray(testRun.testSuites.testSuites)) {
            testRun.testSuites = testRun.status === 2 ? '5/5 passed' : '4/5 passed';
        }

        return testRun;
    });
    setData(allTestRuns);
}

export const TestStatus = ({ value, row }) => {
    const [data, setData] = useState(null);

    let trigger = (
        <small>
            <i>-</i>
        </small>
    );

    if (!value && value !== 0) {
        return trigger;
    }

    const status = parseInt(value, 10);
    const linkProps = {
        href: '#',
        className: style.testStatusTrigger,
    };

    if (status === 1) {
        trigger = (
            <a {...linkProps}>
                {renderSpinner()}
                <span>In Progress</span>
            </a>
        );
    }

    if (status === 2) {
        trigger = (
            <a {...linkProps}>
                <span role="img" aria-label="success">
                    ✅
                </span>{' '}
                <span>Success</span>
            </a>
        );
    }

    if (status === 3) {
        trigger = (
            <a {...linkProps}>
                <span role="img" aria-label="failure">
                    ❌
                </span>{' '}
                <span>Failed</span>
            </a>
        );
    }

    const testableId = _get(row, 'testRun.testableId');
    const testableType = _get(row, 'testRun.testableType');
    if (!testableId || !testableType) {
        return trigger;
    }

    const testableDisplayName = testableType.slice(testableType.lastIndexOf('\\') + 1);

    let isTooltip = [1, 2, 3].includes(status);
    if (isTooltip) {
        trigger = (
            <Tooltip title={`See details about all Test Runs for this ${testableDisplayName}`}>{trigger}</Tooltip>
        );
    }

    return (
        <Modal
            title={`Test runs for ${testableDisplayName} #${testableId}`}
            size="calc(100vw - 80px)"
            trigger={trigger}
            render={({ closeModal }) => <AllTestRuns closeModal={closeModal} data={data} />}
            afterShow={() => {
                fetchAllTestRuns(testableId, testableType, setData);
            }}
        />
    );
};

export const ModalView = ({ value, row, columnName: col, columnRenderParams: params }) => {
    let buttontext = 'Open';
    let modaltitle = col;

    if (col in params) {
        if ('buttontext' in params[col]) {
            buttontext = params[col].buttontext;
        }
        if ('title' in params[col]) {
            modaltitle = params[col].title;
        }
    }
    if (row.status > 1) {
        return (
            <Modal
                trigger={
                    <button type="button" className="btn btn-secondary">
                        {buttontext}
                    </button>
                }
                size="1200px"
                title={modaltitle}
                render={({ closeModal }) => <p className={style.consoleModal}>{value}</p>}
            />
        );
    }
    return (
        <Modal
            trigger={
                <button type="button" className="btn btn-secondary">
                    {buttontext}
                </button>
            }
            size="1200px"
            title={modaltitle}
            render={({ closeModal }) => (
                <p className={style.consoleModal}>
                    {value}
                    <br />
                    <Spinner options={{ position: 'relative', top: '0.7em', left: '0.7em' }} />
                    <br />
                </p>
            )}
        />
    );
};

function formatSeconds(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export const Runtime = ({ value }) => {
    if (value > 0) {
        return formatSeconds(value);
    }
    return value;
};
